<template>
  <div class="your-stuff-page-header" data-name="YourStuffPageHeader">
    <div class="your-stuff-page-header-title-row">
      <div class="your-stuff-page-header-title">
        Your Stuff
      </div>
      <HelpMenu />
    </div>
    <HorizontalMenu
      :items="menuItems"
      size="small"
      :bottomLine="menuBottomLine"
      :fixedTabs="false"
      :value="selectedTab"
      @onChange="handleTabChange"
      :hideLines="menuHideLines"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import HorizontalMenu from '@components/HorizontalMenu/HorizontalMenu.vue';
import HelpMenu from '@components/HelpMenu/HelpMenu.vue';

const props = defineProps({
  value: {
    type: Number,
    default: 0
  },
  showDirectoriesTab: {
    type: Boolean,
    default: false
  },
  showProceduresTab: {
    type: Boolean,
    default: false
  },
  coachInspectCount: {
    type: Number,
    default: 0
  },
  masterBadgeCount: {
    type: Number,
    default: 0
  },
  /** ER-11463 — Rule 1: hide "As an Architect" for an Access with no current Architect badge. */
  showMasterTab: {
    type: Boolean,
    default: true
  },
  pendingTasksCount: {
    type: Number,
    default: 0
  },
  activeProceduresCount: {
    type: Number,
    default: 0
  },
  showCoachTab: {
    type: Boolean,
    default: true
  },
  menuHideLines: {
    type: Boolean,
    default: true
  },
  menuBottomLine: {
    type: Boolean,
    default: false
  }
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;

/** idea-MDP-378: refactored Your Stuff / tasks UX — surfaces pending task count on the nav when enabled. */
const ideaMDP378 = computed(() =>
  Boolean(proxy?.$store?.getters?.['featureFlags/ideaMDP378'])
);

/** idea-MDP-438: Your Stuff > Your Dashboard tab (MDP-438). */
const ideaMDP438 = computed(() =>
  Boolean(proxy?.$store?.getters?.['featureFlags/ideaMDP438'])
);

const emit = defineEmits(['tab-change']);

const selectedTab = computed(() => props.value);

/**
 * Order must match `YourStuffPage.tabRoutes`: [Your Dashboard if ideaMDP438] → Employee → Coach → Master
 * → Your Tasks → [Procedures] → [Directories] → Profile (always last).
 */
const menuItems = computed(() => {
  const mdp = ideaMDP378.value;
  const items = [];
  if (ideaMDP438.value) {
    items.push({ title: 'Your Dashboard', count: null });
  }
  items.push({ title: 'As Employee', count: null });
  if (props.showCoachTab) {
    items.push({
      title: 'As a Coach',
      count: mdp ? props.coachInspectCount : null,
    });
  }
  if (props.showMasterTab) {
    items.push({
      title: 'As an Architect',
      count: mdp ? props.masterBadgeCount : null,
    });
  }
  items.push({
    title: 'Your Tasks',
    count: props.pendingTasksCount > 0 ? props.pendingTasksCount : null,
  });
  if (props.showProceduresTab) {
    items.push({ title: 'Procedures', count: props.activeProceduresCount });
  }
  if (props.showDirectoriesTab) {
    items.push({ title: 'Directories', count: null });
  }
  items.push({ title: 'Your Profile', count: null });
  return items;
});

function handleTabChange(value) {
  emit('tab-change', value);
}
</script>
<style scoped>
.your-stuff-page-header-title-row {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-left: 10px;
  margin-bottom: 10px;
}

.your-stuff-page-header-title {
  flex: 1;
  min-width: 0;
  font-family: Inter;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #101828;
}

</style>
